import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

const fmtBal = (c) => {
  if (!c) return "—";
  if (c.balance == null) return "—";
  if (c.balance < 0) return "∞";
  return `${Number(c.balance).toLocaleString()} / ${Number(c.starting_balance || 0).toLocaleString()}`;
};

export default function AdminPanel() {
  const [orgs, setOrgs] = useState([]);
  const [dailyCredits, setDailyCredits] = useState([]);
  const [plans, setPlans] = useState([]);
  const [busyOrg, setBusyOrg] = useState(null);
  const [snapBusy, setSnapBusy] = useState(false);

  // AI model config
  const [aiModels, setAiModels] = useState([]);
  const [aiTasks, setAiTasks] = useState([]);
  const [aiCurrent, setAiCurrent] = useState({});
  const [busyTask, setBusyTask] = useState(null);

  const ORGS_PER_PAGE = 10;
  const CREDITS_PER_PAGE = 12;

  const [orgPage, setOrgPage] = useState(1);
  const [creditsPage, setCreditsPage] = useState(1);

  const load = async () => {
    const [o, dc, mc] = await Promise.all([
      api.get("/admin/orgs"),
      api.get("/admin/daily-credits?days=30"),
      api.get("/ai/model-config"),
    ]);
    setOrgs(o.data.orgs || []);
    setPlans(o.data.plans || []);
    setDailyCredits(dc.data.rows || []);
    setAiModels(mc.data.available || []);
    setAiTasks(mc.data.tasks || []);
    setAiCurrent(mc.data.current || {});

    setOrgPage(1);
    setCreditsPage(1);
  };

  useEffect(() => { load(); }, []);

  const refreshSnapshot = async () => {
    setSnapBusy(true);
    try {
      const r = await api.post("/admin/snapshot-credits-now");
      toast.success(`Snapshot refreshed — ${r.data?.orgs_recorded ?? 0} orgs`);
      await load();
    } catch {
      toast.error("Snapshot refresh failed");
    } finally {
      setSnapBusy(false);
    }
  };

  const changePlan = async (org, newPlanId) => {
    if (!newPlanId || newPlanId === org.credits?.plan_id) return;
    const cur = org.credits?.plan_id || "—";
    if (!window.confirm(
      `Change '${org.name}' from plan '${cur}' to '${newPlanId}'?\n\nThis resets their credit balance to the new plan's starting amount.`
    )) return;
    setBusyOrg(org.id);
    try {
      await api.patch(`/admin/orgs/${org.id}/plan`, { plan_id: newPlanId });
      toast.success(`Plan changed to ${newPlanId} for ${org.name}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Plan change failed");
    } finally {
      setBusyOrg(null);
    }
  };

  const runResetSweep = async () => {
    try {
      const r = await api.post("/admin/credits/reset-sweep");
      toast.success(`Reset sweep complete. ${r.data.reset_count} org(s) reset.`);
      await load();
    } catch {
      toast.error("Reset sweep failed");
    }
  };

  const changeAiModel = async (taskId, modelId) => {
    if (!modelId || modelId === aiCurrent[taskId]) return;
    setBusyTask(taskId);
    try {
      await api.patch("/ai/model-config", { task: taskId, model_id: modelId });
      setAiCurrent((prev) => ({ ...prev, [taskId]: modelId }));
      toast.success("AI model updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Model change failed");
    } finally {
      setBusyTask(null);
    }
  };

  const totalOrgPages = Math.ceil(orgs.length / ORGS_PER_PAGE);

  const paginatedOrgs = orgs.slice(
    (orgPage - 1) * ORGS_PER_PAGE,
    orgPage * ORGS_PER_PAGE
  );

  const totalCreditPages = Math.ceil(
    dailyCredits.length / CREDITS_PER_PAGE
  );

  const paginatedCredits = dailyCredits.slice(
    (creditsPage - 1) * CREDITS_PER_PAGE,
    creditsPage * CREDITS_PER_PAGE
  );

  // group AI tasks by "where" for the config panel, preserving List 1 order
  const aiTasksByWhere = aiTasks.reduce((acc, t) => {
    (acc[t.where] ||= []).push(t);
    return acc;
  }, {});

  return (
    <div className="p-8 lg:p-12" data-testid="admin-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="type-eyebrow text-[#64748B]">ORL Admin</div>
          <h1 className="type-h1 text-[#0F1F3D] mt-3">Cross-org control</h1>
        </div>
        {/* <button
          onClick={runResetSweep}
          data-testid="admin-reset-sweep"
          className="px-4 py-2.5 rounded-md border border-[#0F1F3D] text-[#0F1F3D] type-body-sm font-medium hover:bg-[#F1F5F9]"
        >
          Run credit reset sweep
        </button> */}
      </div>

      <div className="mt-10 grid grid-cols-1 xl:grid-cols-1 gap-6">
        {/* Orgs table */}
        <section className="xl:col-span-12 bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
          <div className="px-6 py-5 border-b border-[#E2E8F0]">
            <div className="type-eyebrow text-[#64748B]">Organisations</div>
            <div className="type-h3 text-[#0F1F3D] mt-1">All orgs ({orgs.length})</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px]">
              <thead className="bg-[#F8F9FA] type-eyebrow text-[#64748B]">
                <tr>
                  <th className="text-left px-6 py-3">Name</th>
                  <th className="text-left px-6 py-3">Plan</th>
                  {/* <th className="text-right px-6 py-3">Credits</th> */}
                  <th className="text-right px-6 py-3">Users</th>
                  <th className="text-right px-6 py-3">Projects</th>
                  <th className="text-right px-6 py-3">Results</th>
                </tr>
              </thead>
              <tbody>
                {paginatedOrgs.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-6 py-10 text-center text-[#94A3B8]"
                    >
                      No organisations found.
                    </td>
                  </tr>
                ) : (
                  paginatedOrgs.map((o) => {
                    const planId = o.credits?.plan_id || o.plan || "trial";

                    return (
                      <tr
                        key={o.id}
                        className="border-t border-[#E2E8F0]"
                        data-testid={`admin-org-${o.id}`}
                      >
                        <td className="px-5 py-3 font-medium text-[#0F1F3D]">
                          <div>{o.name}</div>

                          {o.credits_exhausted ? (
                            <div className="text-[10px] uppercase tracking-wider text-[#B91C1C] font-bold mt-0.5">
                              Credits exhausted
                            </div>
                          ) : o.critical_credits_warning ? (
                            <div className="text-[10px] uppercase tracking-wider text-[#B91C1C] font-bold mt-0.5">
                              Critical
                            </div>
                          ) : o.low_credits_warning ? (
                            <div className="text-[10px] uppercase tracking-wider text-[#92400E] font-bold mt-0.5">
                              Low balance
                            </div>
                          ) : null}
                        </td>

                        <td className="px-5 py-3">
                          <select
                            value={planId}
                            disabled={busyOrg === o.id}
                            onChange={(e) => changePlan(o, e.target.value)}
                            data-testid={`admin-org-plan-select-${o.id}`}
                            className="px-2.5 py-1.5 rounded-md border border-[#E2E8F0] bg-white text-sm"
                          >
                            {plans.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.label}
                              </option>
                            ))}
                          </select>
                        </td>

                        {/* <td
                          className="px-5 py-3 text-right font-mono-tab text-xs text-[#475569]"
                          data-testid={`admin-org-credits-${o.id}`}
                        >
                          {fmtBal(o.credits)}
                        </td> */}

                        <td className="px-5 py-3 text-right font-mono-tab text-sm">
                          {o.users_count}
                        </td>

                        <td className="px-5 py-3 text-right font-mono-tab text-sm">
                          {o.projects_count}
                        </td>

                        <td className="px-5 py-3 text-right font-mono-tab text-sm">
                          {o.results_count}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between px-6 py-4 border-t border-[#E2E8F0]">
            <div className="text-sm text-[#64748B]">
              Showing {(orgPage - 1) * ORGS_PER_PAGE + 1}–
              {Math.min(orgPage * ORGS_PER_PAGE, orgs.length)} of {orgs.length}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setOrgPage((p) => Math.max(1, p - 1))}
                disabled={orgPage === 1}
                className="px-3 py-2 border rounded-md disabled:opacity-50"
              >
                Previous
              </button>

              {Array.from({ length: totalOrgPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => setOrgPage(i + 1)}
                  className={`w-9 h-9 rounded-md border ${orgPage === i + 1
                    ? "bg-[#0F1F3D] text-white border-[#0F1F3D]"
                    : "border-[#CBD5E1] hover:bg-[#F8FAFC]"
                    }`}
                >
                  {i + 1}
                </button>
              ))}

              <button
                onClick={() => setOrgPage((p) => Math.min(totalOrgPages, p + 1))}
                disabled={orgPage === totalOrgPages}
                className="px-3 py-2 border rounded-md disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </section>

        {/* P7 — Daily credits by org (replaces Recent Runs) */}
        {/* <section className="xl:col-span-4 bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="admin-daily-credits">
          <div className="px-6 py-5 border-b border-[#E2E8F0] flex items-start justify-between gap-3">
            <div>
              <div className="type-eyebrow text-[#64748B]">Cross-org consumption</div>
              <div className="type-h3 text-[#0F1F3D] mt-1">Daily credits by org</div>
              <div className="type-caption text-[#94A3B8] mt-1">Snapshot at server-local noon · last 24h window</div>
            </div>
            <button
              onClick={refreshSnapshot}
              disabled={snapBusy}
              data-testid="admin-snapshot-refresh"
              className="text-xs text-[#0F1F3D] hover:underline disabled:opacity-60"
              title="Take a fresh snapshot now (rolling 24h)"
            >
              {snapBusy ? "Refreshing…" : "Refresh"}
            </button>
          </div>
          <table className="w-full">
            <thead className="bg-[#F8F9FA] type-eyebrow text-[#64748B]">
              <tr>
                <th className="text-left px-6 py-3">Date</th>
                <th className="text-left px-6 py-3">Organisation</th>
                <th className="text-right px-6 py-3">Credits used</th>
              </tr>
            </thead>
            <tbody>
              {paginatedCredits.length === 0 && (
                <tr>
                  <td colSpan="3" className="px-6 py-10 text-center type-body text-[#94A3B8]">
                    No snapshots yet — click Refresh to capture the last 24h.
                  </td>
                </tr>
              )}
              {paginatedCredits.map((r) => (
                <tr key={r.id} className="border-t border-[#E2E8F0]" data-testid={`admin-credits-row-${r.id}`}>
                  <td className="px-6 py-3 type-caption text-[#475569]">{r.snapshot_date}</td>
                  <td className="px-6 py-3 type-body-sm text-[#0F1F3D]">{r.org_name || r.org_id}</td>
                  <td className="px-6 py-3 text-right font-mono-tab text-sm font-bold text-[#92400E]">
                    {Number(r.credits_used).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex flex-col gap-3 px-6 py-4 border-t border-[#E2E8F0] sm:flex-col sm:items-center sm:justify-between">
            <div className="text-sm text-[#64748B] whitespace-nowrap">
              Showing{" "}
              {dailyCredits.length === 0
                ? 0
                : (creditsPage - 1) * CREDITS_PER_PAGE + 1}
              –
              {Math.min(
                creditsPage * CREDITS_PER_PAGE,
                dailyCredits.length
              )}{" "}
              of {dailyCredits.length}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setCreditsPage((p) => Math.max(1, p - 1))}
                disabled={creditsPage === 1}
                className="h-10 px-4 rounded-lg border border-[#CBD5E1] bg-white text-[#475569] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#F8FAFC]"
              >
                Previous
              </button>

              {Array.from({ length: totalCreditPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => setCreditsPage(i + 1)}
                  className={`w-10 h-10 rounded-lg border text-sm font-medium transition ${creditsPage === i + 1
                      ? "bg-[#0F1F3D] border-[#0F1F3D] text-white"
                      : "bg-white border-[#CBD5E1] text-[#0F1F3D] hover:bg-[#F8FAFC]"
                    }`}
                >
                  {i + 1}
                </button>
              ))}

              <button
                onClick={() =>
                  setCreditsPage((p) => Math.min(totalCreditPages, p + 1))
                }
                disabled={creditsPage === totalCreditPages}
                className="h-10 px-4 rounded-lg border border-[#CBD5E1] bg-white text-[#475569] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#F8FAFC]"
              >
                Next
              </button>
            </div>
          </div>
        </section>

        {/* AI model configuration — one dropdown per use case, grouped by where it runs */}
        <section className="xl:col-span-12 bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="admin-ai-model-config">
          <div className="px-6 py-5 border-b border-[#E2E8F0]">
            <div className="type-eyebrow text-[#64748B]">AI configuration</div>
            <div className="type-h3 text-[#0F1F3D] mt-1">Model per use case</div>
            <div className="type-caption text-[#94A3B8] mt-1">
              Choose which model handles each AI action across the platform.
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 px-6 py-6">
            {Object.entries(aiTasksByWhere).map(([where, tasks]) => (
              <div key={where} className="border border-[#E2E8F0] rounded-lg overflow-hidden">
                <div className="px-5 py-3 bg-[#F8F9FA] type-eyebrow text-[#64748B]">
                  {where}
                </div>
                <div className="divide-y divide-[#E2E8F0]">
                  {tasks.map((t) => (
                    <div
                      key={t.id}
                      className="flex items-center justify-between gap-4 px-5 py-3.5"
                      data-testid={`admin-ai-task-${t.id}`}
                    >
                      <div className="type-body-sm text-[#0F1F3D] pr-4">{t.label}</div>
                      <select
                        value={aiCurrent[t.id] || ""}
                        disabled={busyTask === t.id}
                        onChange={(e) => changeAiModel(t.id, e.target.value)}
                        data-testid={`admin-ai-model-select-${t.id}`}
                        className="min-w-[220px] px-2.5 py-1.5 rounded-md border border-[#E2E8F0] bg-white text-sm shrink-0"
                      >
                        {aiModels.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}